const SCROLL_THRESHOLD = 8;
const USP_INTERVAL_MS = 4000;
const MESSAGE_MS = 6000;
const USP_ROW_MS = 6000;
const SLIDE_MS = 400;
const MOBILE_QUERY = '(max-width: 1023px)';
const DESKTOP_QUERY = '(min-width: 1024px)';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const HOVER_OPEN_MS = 150;
const HOVER_CLOSE_MS = 300;

const rotateTicker = (ticker) => {
    const message = ticker.querySelector('[data-hdr-message]');
    const uspList = ticker.querySelector('[data-hdr-usps]');
    const usps = uspList ? Array.from(uspList.querySelectorAll('.hdr__usp')) : [];
    const mobile = window.matchMedia(MOBILE_QUERY);
    const reducedMotion = window.matchMedia(REDUCED_MOTION_QUERY);

    let steps = [];
    let index = 0;
    let timer = null;
    let hovered = false;
    let focused = false;

    const buildSteps = () => {
        let uspSteps = [];
        if (mobile.matches) {
            uspSteps = usps.map((usp) => ({ slide: uspList, usp, ms: USP_INTERVAL_MS }));
        } else if (usps.length) {
            uspSteps = [{ slide: uspList, usp: null, ms: USP_ROW_MS }];
        }
        return message ? [{ slide: message, usp: null, ms: MESSAGE_MS }, ...uspSteps] : uspSteps;
    };

    const show = (step) => {
        [message, uspList].forEach((slide) => {
            if (!slide) {
                return;
            }
            const active = slide === step.slide;
            if (!active && slide.classList.contains('is-active')) {
                slide.classList.add('is-leaving');
                window.setTimeout(() => slide.classList.remove('is-leaving'), SLIDE_MS);
            }
            slide.classList.toggle('is-active', active);
        });
        if (step.usp) {
            usps.forEach((usp) => usp.classList.toggle('is-active', usp === step.usp));
        }
    };

    const schedule = () => {
        window.clearTimeout(timer);
        if (hovered || focused || steps.length < 2 || reducedMotion.matches) {
            return;
        }
        timer = window.setTimeout(() => {
            index = (index + 1) % steps.length;
            show(steps[index]);
            schedule();
        }, steps[index].ms);
    };

    const restart = () => {
        steps = buildSteps();
        index = 0;
        if (steps.length) {
            show(steps[0]);
        }
        schedule();
    };

    ticker.addEventListener('mouseenter', () => {
        hovered = true;
        schedule();
    });
    ticker.addEventListener('mouseleave', () => {
        hovered = false;
        schedule();
    });
    ticker.addEventListener('focusin', (event) => {
        focused = true;
        const focusedIndex = steps.findIndex((step) => step.slide.contains(event.target) && !step.usp);
        if (focusedIndex >= 0 && focusedIndex !== index) {
            index = focusedIndex;
            show(steps[index]);
        }
        schedule();
    });
    ticker.addEventListener('focusout', (event) => {
        focused = ticker.contains(event.relatedTarget);
        schedule();
    });
    mobile.addEventListener('change', restart);
    reducedMotion.addEventListener('change', restart);

    restart();
};

const initNav = (header, nav) => {
    const desktop = window.matchMedia(DESKTOP_QUERY);
    const burger = nav.querySelector('[data-hdr-nav-burger]');
    const root = document.getElementById(burger.getAttribute('aria-controls'));
    const toggles = Array.from(nav.querySelectorAll('[data-hdr-nav-toggle]'));
    const isOpen = (button) => button.getAttribute('aria-expanded') === 'true';
    const controlled = (button) => document.getElementById(button.getAttribute('aria-controls'));

    const setPanel = (toggle, open) => {
        toggle.setAttribute('aria-expanded', String(open));
        toggle.parentElement.classList.toggle('is-open', open);
    };
    const closePanels = (except) => toggles.forEach((t) => t !== except && isOpen(t) && setPanel(t, false));

    const setLevel = (drill, open) => {
        drill.setAttribute('aria-expanded', String(open));
        controlled(drill).classList.toggle('navbar__menu--show', open);
    };
    const drawerOpen = () => root.classList.contains('navbar__menu--show');
    const setDrawer = (open) => {
        root.classList.toggle('navbar__menu--show', open);
        document.documentElement.classList.toggle('hdr-scroll-lock', open);
        burger.setAttribute('aria-expanded', String(open));
        burger.setAttribute('aria-label', open ? burger.dataset.labelClose : burger.dataset.labelOpen);
        if (!open) {
            nav.querySelectorAll('[data-hdr-nav-drill][aria-expanded="true"]').forEach((d) => setLevel(d, false));
        }
    };
    const openLevel = (drill) => {
        setLevel(drill, true);
        const back = controlled(drill).querySelector(':scope > .navbar__item--back > [data-hdr-nav-back]');
        if (back) {
            back.focus();
        }
    };
    const closeLevel = (submenu) => {
        const drill = nav.querySelector(`[data-hdr-nav-drill][aria-controls="${submenu.id}"]`);
        if (drill) {
            setLevel(drill, false);
            drill.focus();
        }
    };

    nav.addEventListener('click', (e) => {
        if (e.target.closest('[data-hdr-nav-burger]')) {
            setDrawer(!drawerOpen());
            return;
        }
        const toggle = e.target.closest('[data-hdr-nav-toggle]');
        if (toggle) {
            closePanels(toggle);
            setPanel(toggle, !isOpen(toggle));
            return;
        }
        const caret = e.target.closest('[data-hdr-nav-caret]');
        const drill = e.target.closest('[data-hdr-nav-drill]') || (caret && caret.previousElementSibling);
        if (drill) {
            openLevel(drill);
            return;
        }
        const back = e.target.closest('[data-hdr-nav-back]');
        if (back) {
            closeLevel(back.closest('.navbar__menu'));
        }
    });

    header.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') {
            return;
        }
        if (desktop.matches) {
            const item = e.target.closest('.is-open');
            const toggle = item && item.querySelector(':scope > [data-hdr-nav-toggle]');
            if (toggle) {
                setPanel(toggle, false);
                toggle.focus();
            }
            return;
        }
        const submenu = e.target.closest('.navbar__menu--show');
        if (submenu && submenu !== root) {
            closeLevel(submenu);
        } else if (drawerOpen()) {
            setDrawer(false);
            burger.focus();
        }
    });

    const searchInput = header.querySelector('.hdr__search-input');
    if (searchInput) {
        searchInput.addEventListener('focus', () => drawerOpen() && setDrawer(false));
    }

    // A null relatedTarget means focus left the page or landed on nothing focusable; outside clicks cover that case.
    nav.addEventListener('focusout', (e) => {
        const next = e.relatedTarget;
        if (next) {
            toggles.forEach((t) => isOpen(t) && !t.parentElement.contains(next) && setPanel(t, false));
        }
    });
    header.addEventListener('focusout', (e) => {
        if (e.relatedTarget && !header.contains(e.relatedTarget) && drawerOpen()) {
            setDrawer(false);
        }
    });
    // Hover intent: opening waits and closing lingers, so a diagonal path over a neighbour item neither switches nor closes the menu.
    let hovered = null;
    let pending;
    let hoverTimer = 0;
    const setHover = (item) => {
        pending = undefined;
        if (hovered) {
            hovered.classList.remove('is-hover');
        }
        hovered = item;
        if (item) {
            item.classList.add('is-hover');
        }
    };
    const hoverLater = (item, delay) => {
        if (item === pending) {
            return;
        }
        window.clearTimeout(hoverTimer);
        pending = item;
        hoverTimer = window.setTimeout(() => setHover(item), delay);
    };
    const keepHover = () => {
        window.clearTimeout(hoverTimer);
        pending = undefined;
    };

    document.addEventListener('click', (e) => {
        if (!nav.contains(e.target)) {
            closePanels();
            keepHover();
            setHover(null);
        }
    });

    nav.addEventListener('mouseover', (e) => {
        if (!desktop.matches) {
            return;
        }
        const item = e.target.closest('.navbar > .navbar__menu > .navbar__item');
        if (!item) {
            hoverLater(null, HOVER_CLOSE_MS);
            return;
        }
        closePanels(item.querySelector(':scope > [data-hdr-nav-toggle]'));
        if (item === hovered) {
            keepHover();
        } else {
            hoverLater(item, HOVER_OPEN_MS);
        }
    });
    nav.addEventListener('mouseleave', () => hoverLater(null, HOVER_CLOSE_MS));

    desktop.addEventListener('change', () => {
        closePanels();
        keepHover();
        setHover(null);
        setDrawer(false);
    });
};

document.addEventListener('DOMContentLoaded', () => {
    const header = document.querySelector('[data-hdr]');
    if (!header) {
        return;
    }

    const root = document.documentElement;
    const topBar = header.querySelector('.hdr__top');
    const isCollapsed = () => header.classList.contains('hdr--collapsed');

    // The spacer must keep the expanded height, so measure only when expanded and not mid-transition.
    const syncHeights = () => {
        if (isCollapsed()) {
            return;
        }
        root.style.setProperty('--hdr-h', header.offsetHeight + 'px');
        root.style.setProperty('--hdr-top-h', (topBar ? topBar.offsetHeight : 0) + 'px');
    };
    syncHeights();
    window.addEventListener('resize', syncHeights);
    header.addEventListener('transitionend', (e) => {
        if (e.target === header) {
            syncHeights();
        }
    });
    window.addEventListener('load', syncHeights);

    const nav = header.querySelector('.hdr__nav');
    const bag = header.querySelector('.hdr__bag');
    const desktop = window.matchMedia(DESKTOP_QUERY);
    const syncOpenState = () => {
        const menuOpen = !!nav && nav.querySelector(':scope > .navbar__menu--show') !== null;
        const cartOpen = !!bag && bag.querySelector('.cart__info:not(.cart__info--hidden)') !== null;
        const panelOpen = !!nav && desktop.matches &&
            nav.querySelector('.navbar > .navbar__menu > :is(.is-hover, .is-open) > .navbar__menu-container') !== null;
        header.classList.toggle('hdr--menu-open', menuOpen);
        header.classList.toggle('hdr--solid', menuOpen || cartOpen || panelOpen);
    };
    const observeOptions = { subtree: true, childList: true, attributes: true, attributeFilter: ['class'] };
    [nav, bag].filter(Boolean).forEach((el) => new MutationObserver(syncOpenState).observe(el, observeOptions));

    if (nav && nav.hasAttribute('data-hdr-nav')) {
        initNav(header, nav);
    }

    const expand = () => header.classList.remove('hdr--collapsed');
    header.addEventListener('click', (e) => {
        if (e.target.closest('.navbar__icon--menu')) {
            expand();
        }
    });

    // The Hello Retail search overlay is offset by the expanded header height (--hdr-h).
    const searchInput = header.querySelector('.hdr__search-input');
    if (searchInput) {
        searchInput.addEventListener('focus', expand);
    }

    const isPinnedOpen = () =>
        header.querySelector('.navbar__menu--show') !== null ||
        header.querySelector('.cart__info:not(.cart__info--hidden)') !== null ||
        // Only keyboard focus pins the header (WCAG 2.4.11); a clicked button keeps focus but should not stop it collapsing.
        (header.contains(document.activeElement) && document.activeElement.matches(':focus-visible'));

    let lastY = Math.max(window.scrollY, 0);
    let ticking = false;

    const update = () => {
        ticking = false;
        const y = Math.max(window.scrollY, 0);
        header.classList.toggle('hdr--scrolled', y > 0);

        const delta = y - lastY;
        if (Math.abs(delta) < SCROLL_THRESHOLD) {
            return;
        }
        if (delta > 0 && y > header.offsetHeight && !isPinnedOpen()) {
            header.classList.add('hdr--collapsed');
        } else if (delta < 0) {
            expand();
        }
        lastY = y;
    };

    window.addEventListener('scroll', () => {
        if (!ticking) {
            ticking = true;
            window.requestAnimationFrame(update);
        }
    }, { passive: true });

    update();

    const ticker = header.querySelector('[data-hdr-ticker]');
    if (ticker) {
        rotateTicker(ticker);
    }
});
